import {
  CHUNK_SIZE,
  chunkKeyInt,
  TILE_SIZE,
  TileType,
  type TileTypeValue,
} from '@dreamgame/shared';
import { Graphics } from 'pixi.js';
import { type ChunkWithObjects, getAllChunks } from '../../state/chunks';
import { WATER_EFFECTS } from '../constants';
import type { VisibleChunk } from './types';

// ============================================================================
// WaterEffectsLayer - Renders pseudo-3D water depth effects
// Optimized: batched drawing, simplified shadows, skip interior tiles
// ============================================================================

let graphics: Graphics;

export const createWaterEffectsLayer = (): Graphics => {
  graphics = new Graphics();
  return graphics;
};

// Water tile types that should have shore shadow effects
const WATER_TILES: ReadonlySet<TileTypeValue> = new Set([
  TileType.DEEP_WATER,
  TileType.WATER,
  TileType.SHALLOW_WATER,
  TileType.RIVER,
  TileType.FROZEN_WATER,
  TileType.SWAMP_WATER,
]);

// Check if a tile is water
const isWaterTile = (tileType: TileTypeValue): boolean => {
  return WATER_TILES.has(tileType);
};

// Check if a tile is solid land (not water, not void)
const isLandTile = (tileType: TileTypeValue): boolean => {
  return tileType !== TileType.VOID && !isWaterTile(tileType);
};

// PERFORMANCE: Preallocated arrays to avoid GC
type Rect = { x: number; y: number; w: number; h: number };
const _shadowRects: Array<Rect> = [];
const _highlightRects: Array<Rect> = [];
const _cornerRects: Array<Rect> = [];

// Ensure we have enough preallocated rects
const ensureCapacity = (arr: Array<Rect>, needed: number): void => {
  while (arr.length < needed) {
    arr.push({ x: 0, y: 0, w: 0, h: 0 });
  }
};

// Get neighbor tile, handling chunk boundaries efficiently
const getNeighborTile = (
  chunkX: number,
  chunkY: number,
  localX: number,
  localY: number,
  offsetX: number,
  offsetY: number,
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>,
  currentChunk: ChunkWithObjects
): TileTypeValue => {
  const nx = localX + offsetX;
  const ny = localY + offsetY;

  // Fast path: neighbor is in same chunk
  if (nx >= 0 && nx < CHUNK_SIZE && ny >= 0 && ny < CHUNK_SIZE) {
    const index = ny * CHUNK_SIZE + nx;
    return (currentChunk.tiles[index] ?? TileType.VOID) as TileTypeValue;
  }

  // Slow path: neighbor is in different chunk
  let cx = chunkX;
  let cy = chunkY;
  let nnx = nx;
  let nny = ny;

  if (nnx < 0) {
    cx--;
    nnx += CHUNK_SIZE;
  } else if (nnx >= CHUNK_SIZE) {
    cx++;
    nnx -= CHUNK_SIZE;
  }

  if (nny < 0) {
    cy--;
    nny += CHUNK_SIZE;
  } else if (nny >= CHUNK_SIZE) {
    cy++;
    nny -= CHUNK_SIZE;
  }

  // PERFORMANCE: Reuse preallocated temp to avoid object allocation per lookup
  _chunkKeyTemp.x = cx;
  _chunkKeyTemp.y = cy;
  const neighborChunk = loadedChunks.get(chunkKeyInt(_chunkKeyTemp));
  if (!neighborChunk) {
    return TileType.VOID;
  }

  const index = nny * CHUNK_SIZE + nnx;
  return (neighborChunk.tiles[index] ?? TileType.VOID) as TileTypeValue;
};

// Add a rect to the array at the given index
const addRect = ({
  arr,
  index,
  x,
  y,
  w,
  h,
}: {
  arr: Array<Rect>;
  index: number;
  x: number;
  y: number;
  w: number;
  h: number;
}): void => {
  const rect = arr[index];
  if (rect) {
    rect.x = x;
    rect.y = y;
    rect.w = w;
    rect.h = h;
  }
};

// Cardinal neighbor info for a tile
type NeighborInfo = {
  top: TileTypeValue;
  bottom: TileTypeValue;
  left: TileTypeValue;
  right: TileTypeValue;
};

// Diagonal neighbor info for a tile
type DiagonalNeighborInfo = {
  topLeft: TileTypeValue;
  topRight: TileTypeValue;
  bottomLeft: TileTypeValue;
  bottomRight: TileTypeValue;
};

// PERFORMANCE: Preallocated scratch objects for hot-path neighbor lookups.
// Caller must use the result before the next call to the same function.
const _neighbors: NeighborInfo = {
  top: TileType.VOID as TileTypeValue,
  bottom: TileType.VOID as TileTypeValue,
  left: TileType.VOID as TileTypeValue,
  right: TileType.VOID as TileTypeValue,
};
const _diagonals: DiagonalNeighborInfo = {
  topLeft: TileType.VOID as TileTypeValue,
  topRight: TileType.VOID as TileTypeValue,
  bottomLeft: TileType.VOID as TileTypeValue,
  bottomRight: TileType.VOID as TileTypeValue,
};

// PERFORMANCE: Preallocated return objects for per-tile processing
const _waterTileResult = { shadowCount: 0, cornerCount: 0 };
const _chunkResult = { shadowCount: 0, highlightCount: 0, cornerCount: 0 };

// PERFORMANCE: Preallocated temp for chunkKeyInt in cross-chunk lookups
const _chunkKeyTemp = { x: 0, y: 0 };

// PERFORMANCE: Frame-level cache. Water effects only change when visible chunks
// or chunk data updates, so skip the expensive per-tile iteration on most frames.
let _cachedChunksRef: ReadonlyMap<number, ChunkWithObjects> | null = null;
let _cachedChunksSize = 0;
let _cachedVisibleHash = 0;
let _cachedShadowCount = 0;
let _cachedHighlightCount = 0;
let _cachedCornerCount = 0;

// Hash visible chunk coordinates to detect viewport changes
const computeVisibleChunksHash = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): number => {
  let hash = visibleChunks.length;
  for (let i = 0; i < visibleChunks.length; i++) {
    const vc = visibleChunks[i];
    if (vc) {
      hash = (hash * 31 + vc.x) | 0;
      hash = (hash * 31 + vc.y) | 0;
    }
  }
  return hash;
};

// PERFORMANCE: Mutates preallocated _neighbors; caller must use before next call.
const getCardinalNeighbors = ({
  chunkX,
  chunkY,
  localX,
  localY,
  loadedChunks,
  chunk,
}: {
  chunkX: number;
  chunkY: number;
  localX: number;
  localY: number;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
  chunk: ChunkWithObjects;
}): NeighborInfo => {
  _neighbors.top = getNeighborTile(chunkX, chunkY, localX, localY, 0, -1, loadedChunks, chunk);
  _neighbors.bottom = getNeighborTile(chunkX, chunkY, localX, localY, 0, 1, loadedChunks, chunk);
  _neighbors.left = getNeighborTile(chunkX, chunkY, localX, localY, -1, 0, loadedChunks, chunk);
  _neighbors.right = getNeighborTile(chunkX, chunkY, localX, localY, 1, 0, loadedChunks, chunk);
  return _neighbors;
};

// PERFORMANCE: Mutates preallocated _diagonals; caller must use before next call.
const getDiagonalNeighbors = ({
  chunkX,
  chunkY,
  localX,
  localY,
  loadedChunks,
  chunk,
}: {
  chunkX: number;
  chunkY: number;
  localX: number;
  localY: number;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
  chunk: ChunkWithObjects;
}): DiagonalNeighborInfo => {
  _diagonals.topLeft = getNeighborTile(chunkX, chunkY, localX, localY, -1, -1, loadedChunks, chunk);
  _diagonals.topRight = getNeighborTile(chunkX, chunkY, localX, localY, 1, -1, loadedChunks, chunk);
  _diagonals.bottomLeft = getNeighborTile(
    chunkX,
    chunkY,
    localX,
    localY,
    -1,
    1,
    loadedChunks,
    chunk
  );
  _diagonals.bottomRight = getNeighborTile(
    chunkX,
    chunkY,
    localX,
    localY,
    1,
    1,
    loadedChunks,
    chunk
  );
  return _diagonals;
};

// Collect corner shadow rects for water tiles with only diagonal land neighbors
const collectCornerShadows = ({
  diagonals,
  worldX,
  worldY,
  cornerShadowSize,
  startIndex,
}: {
  diagonals: DiagonalNeighborInfo;
  worldX: number;
  worldY: number;
  cornerShadowSize: number;
  startIndex: number;
}): number => {
  let count = startIndex;
  ensureCapacity(_cornerRects, count + 4);

  if (isLandTile(diagonals.topLeft)) {
    addRect({
      arr: _cornerRects,
      index: count++,
      x: worldX,
      y: worldY,
      w: cornerShadowSize,
      h: cornerShadowSize,
    });
  }
  if (isLandTile(diagonals.topRight)) {
    addRect({
      arr: _cornerRects,
      index: count++,
      x: worldX + TILE_SIZE - cornerShadowSize,
      y: worldY,
      w: cornerShadowSize,
      h: cornerShadowSize,
    });
  }
  if (isLandTile(diagonals.bottomLeft)) {
    addRect({
      arr: _cornerRects,
      index: count++,
      x: worldX,
      y: worldY + TILE_SIZE - cornerShadowSize,
      w: cornerShadowSize,
      h: cornerShadowSize,
    });
  }
  if (isLandTile(diagonals.bottomRight)) {
    addRect({
      arr: _cornerRects,
      index: count++,
      x: worldX + TILE_SIZE - cornerShadowSize,
      y: worldY + TILE_SIZE - cornerShadowSize,
      w: cornerShadowSize,
      h: cornerShadowSize,
    });
  }
  return count;
};

// Collect shadow rects for water tiles adjacent to land
const collectShadowRects = ({
  neighbors,
  worldX,
  worldY,
  shadowHeight,
  startIndex,
}: {
  neighbors: NeighborInfo;
  worldX: number;
  worldY: number;
  shadowHeight: number;
  startIndex: number;
}): number => {
  let count = startIndex;
  ensureCapacity(_shadowRects, count + 4);

  if (isLandTile(neighbors.top)) {
    addRect({
      arr: _shadowRects,
      index: count++,
      x: worldX,
      y: worldY,
      w: TILE_SIZE,
      h: shadowHeight,
    });
  }
  if (isLandTile(neighbors.bottom)) {
    addRect({
      arr: _shadowRects,
      index: count++,
      x: worldX,
      y: worldY + TILE_SIZE - shadowHeight,
      w: TILE_SIZE,
      h: shadowHeight,
    });
  }
  if (isLandTile(neighbors.left)) {
    addRect({
      arr: _shadowRects,
      index: count++,
      x: worldX,
      y: worldY,
      w: shadowHeight,
      h: TILE_SIZE,
    });
  }
  if (isLandTile(neighbors.right)) {
    addRect({
      arr: _shadowRects,
      index: count++,
      x: worldX + TILE_SIZE - shadowHeight,
      y: worldY,
      w: shadowHeight,
      h: TILE_SIZE,
    });
  }
  return count;
};

// Collect highlight rects for land tiles adjacent to water
const collectHighlightRects = ({
  neighbors,
  worldX,
  worldY,
  highlightWidth,
  startIndex,
}: {
  neighbors: NeighborInfo;
  worldX: number;
  worldY: number;
  highlightWidth: number;
  startIndex: number;
}): number => {
  let count = startIndex;
  ensureCapacity(_highlightRects, count + 4);

  if (isWaterTile(neighbors.bottom)) {
    addRect({
      arr: _highlightRects,
      index: count++,
      x: worldX,
      y: worldY + TILE_SIZE - highlightWidth,
      w: TILE_SIZE,
      h: highlightWidth,
    });
  }
  if (isWaterTile(neighbors.top)) {
    addRect({
      arr: _highlightRects,
      index: count++,
      x: worldX,
      y: worldY,
      w: TILE_SIZE,
      h: highlightWidth,
    });
  }
  if (isWaterTile(neighbors.right)) {
    addRect({
      arr: _highlightRects,
      index: count++,
      x: worldX + TILE_SIZE - highlightWidth,
      y: worldY,
      w: highlightWidth,
      h: TILE_SIZE,
    });
  }
  if (isWaterTile(neighbors.left)) {
    addRect({
      arr: _highlightRects,
      index: count++,
      x: worldX,
      y: worldY,
      w: highlightWidth,
      h: TILE_SIZE,
    });
  }
  return count;
};

// Process a single water tile and collect effects
const processWaterTile = ({
  chunkX,
  chunkY,
  localX,
  localY,
  worldX,
  worldY,
  loadedChunks,
  chunk,
  neighbors,
  shadowHeight,
  cornerShadowSize,
  shadowCount,
  cornerCount,
}: {
  chunkX: number;
  chunkY: number;
  localX: number;
  localY: number;
  worldX: number;
  worldY: number;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
  chunk: ChunkWithObjects;
  neighbors: NeighborInfo;
  shadowHeight: number;
  cornerShadowSize: number;
  shadowCount: number;
  cornerCount: number;
}): { shadowCount: number; cornerCount: number } => {
  const hasLandNeighbor =
    isLandTile(neighbors.top) ||
    isLandTile(neighbors.bottom) ||
    isLandTile(neighbors.left) ||
    isLandTile(neighbors.right);

  // PERFORMANCE: Mutate preallocated _waterTileResult instead of allocating
  if (!hasLandNeighbor) {
    const diagonals = getDiagonalNeighbors({ chunkX, chunkY, localX, localY, loadedChunks, chunk });
    const hasDiagonalLand =
      isLandTile(diagonals.topLeft) ||
      isLandTile(diagonals.topRight) ||
      isLandTile(diagonals.bottomLeft) ||
      isLandTile(diagonals.bottomRight);

    if (!hasDiagonalLand) {
      _waterTileResult.shadowCount = shadowCount;
      _waterTileResult.cornerCount = cornerCount;
      return _waterTileResult;
    }

    _waterTileResult.shadowCount = shadowCount;
    _waterTileResult.cornerCount = collectCornerShadows({
      diagonals,
      worldX,
      worldY,
      cornerShadowSize,
      startIndex: cornerCount,
    });
    return _waterTileResult;
  }

  _waterTileResult.shadowCount = collectShadowRects({
    neighbors,
    worldX,
    worldY,
    shadowHeight,
    startIndex: shadowCount,
  });
  _waterTileResult.cornerCount = cornerCount;
  return _waterTileResult;
};

// Process a single land tile and collect effects
const processLandTile = ({
  neighbors,
  worldX,
  worldY,
  highlightWidth,
  highlightCount,
}: {
  neighbors: NeighborInfo;
  worldX: number;
  worldY: number;
  highlightWidth: number;
  highlightCount: number;
}): number => {
  const hasWaterNeighbor =
    isWaterTile(neighbors.top) ||
    isWaterTile(neighbors.bottom) ||
    isWaterTile(neighbors.left) ||
    isWaterTile(neighbors.right);

  if (!hasWaterNeighbor) {
    return highlightCount;
  }

  return collectHighlightRects({
    neighbors,
    worldX,
    worldY,
    highlightWidth,
    startIndex: highlightCount,
  });
};

// Process a single chunk for water effects
const processChunk = ({
  chunkX,
  chunkY,
  chunk,
  loadedChunks,
  shadowHeight,
  highlightWidth,
  cornerShadowSize,
  shadowCount,
  highlightCount,
  cornerCount,
}: {
  chunkX: number;
  chunkY: number;
  chunk: ChunkWithObjects;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
  shadowHeight: number;
  highlightWidth: number;
  cornerShadowSize: number;
  shadowCount: number;
  highlightCount: number;
  cornerCount: number;
}): { shadowCount: number; highlightCount: number; cornerCount: number } => {
  const chunkWorldX = chunkX * CHUNK_SIZE * TILE_SIZE;
  const chunkWorldY = chunkY * CHUNK_SIZE * TILE_SIZE;
  let sc = shadowCount;
  let hc = highlightCount;
  let cc = cornerCount;

  for (let localY = 0; localY < CHUNK_SIZE; localY++) {
    for (let localX = 0; localX < CHUNK_SIZE; localX++) {
      const tileIndex = localY * CHUNK_SIZE + localX;
      const tileType = (chunk.tiles[tileIndex] ?? TileType.VOID) as TileTypeValue;

      if (tileType === TileType.VOID) {
        continue;
      }

      const worldX = chunkWorldX + localX * TILE_SIZE;
      const worldY = chunkWorldY + localY * TILE_SIZE;
      const isWater = isWaterTile(tileType);
      const neighbors = getCardinalNeighbors({
        chunkX,
        chunkY,
        localX,
        localY,
        loadedChunks,
        chunk,
      });

      if (isWater) {
        const result = processWaterTile({
          chunkX,
          chunkY,
          localX,
          localY,
          worldX,
          worldY,
          loadedChunks,
          chunk,
          neighbors,
          shadowHeight,
          cornerShadowSize,
          shadowCount: sc,
          cornerCount: cc,
        });
        sc = result.shadowCount;
        cc = result.cornerCount;
      } else {
        hc = processLandTile({ neighbors, worldX, worldY, highlightWidth, highlightCount: hc });
      }
    }
  }

  // PERFORMANCE: Mutate preallocated _chunkResult instead of allocating
  _chunkResult.shadowCount = sc;
  _chunkResult.highlightCount = hc;
  _chunkResult.cornerCount = cc;
  return _chunkResult;
};

// Draw collected rects in batches
const drawRectBatch = ({
  rects,
  count,
  color,
  alpha,
}: {
  rects: Array<Rect>;
  count: number;
  color: number;
  alpha: number;
}): void => {
  if (count === 0) {
    return;
  }
  graphics.setFillStyle({ color, alpha });
  for (let i = 0; i < count; i++) {
    const rect = rects[i];
    if (rect) {
      graphics.rect(rect.x, rect.y, rect.w, rect.h);
    }
  }
  graphics.fill();
};

export const drawWaterEffects = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  graphics.clear();

  const loadedChunks = getAllChunks();

  // PERFORMANCE: Skip expensive per-tile iteration when nothing changed.
  // Water-land boundaries are static once chunks load, so most frames skip entirely.
  // Cache invalidates when: visible chunk set changes, or chunk data updates (new Map ref).
  const visibleHash = computeVisibleChunksHash({ visibleChunks });
  const needsRecompute =
    loadedChunks !== _cachedChunksRef ||
    loadedChunks.size !== _cachedChunksSize ||
    visibleHash !== _cachedVisibleHash;

  let shadowCount: number;
  let highlightCount: number;
  let cornerCount: number;

  if (needsRecompute) {
    const { SHADOW_HEIGHT, HIGHLIGHT_WIDTH, CORNER_SHADOW_SIZE } = WATER_EFFECTS;

    shadowCount = 0;
    highlightCount = 0;
    cornerCount = 0;

    for (const { x: chunkX, y: chunkY } of visibleChunks) {
      _chunkKeyTemp.x = chunkX;
      _chunkKeyTemp.y = chunkY;
      const chunk = loadedChunks.get(chunkKeyInt(_chunkKeyTemp));
      if (!chunk) {
        continue;
      }

      const result = processChunk({
        chunkX,
        chunkY,
        chunk,
        loadedChunks,
        shadowHeight: SHADOW_HEIGHT,
        highlightWidth: HIGHLIGHT_WIDTH,
        cornerShadowSize: CORNER_SHADOW_SIZE,
        shadowCount,
        highlightCount,
        cornerCount,
      });
      shadowCount = result.shadowCount;
      highlightCount = result.highlightCount;
      cornerCount = result.cornerCount;
    }

    _cachedChunksRef = loadedChunks;
    _cachedChunksSize = loadedChunks.size;
    _cachedVisibleHash = visibleHash;
    _cachedShadowCount = shadowCount;
    _cachedHighlightCount = highlightCount;
    _cachedCornerCount = cornerCount;
  } else {
    shadowCount = _cachedShadowCount;
    highlightCount = _cachedHighlightCount;
    cornerCount = _cachedCornerCount;
  }

  const { SHADOW_COLOR, SHADOW_ALPHA_MAX, HIGHLIGHT_COLOR, HIGHLIGHT_ALPHA, CORNER_SHADOW_ALPHA } =
    WATER_EFFECTS;

  drawRectBatch({
    rects: _shadowRects,
    count: shadowCount,
    color: SHADOW_COLOR,
    alpha: SHADOW_ALPHA_MAX * 0.6,
  });
  drawRectBatch({
    rects: _cornerRects,
    count: cornerCount,
    color: SHADOW_COLOR,
    alpha: CORNER_SHADOW_ALPHA * 0.6,
  });
  drawRectBatch({
    rects: _highlightRects,
    count: highlightCount,
    color: HIGHLIGHT_COLOR,
    alpha: HIGHLIGHT_ALPHA,
  });
};
